import { speakerDisplayName } from 'lib/speakerPresentation';
import { getPublicService } from 'lib/siteServicesApi';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getPublicCourseBySlug } from 'lib/siteCoursesApi';
import { getPublicSpeakerBySlug } from 'lib/siteSpeakersApi';
import seoConfig from 'data/seoRoutes.json';
import policy from 'lib/seoRoutePolicy';
import { getPublishedBlogPostBySlug } from 'lib/blogCms';
import { getPublishedResourcePageBySlug } from 'lib/resourceCms';

const FALLBACK_SEO_CONFIG = {
  siteUrl: 'https://magellan.international',
  siteName: 'Magellan',
  defaultImage: 'https://magellan.international/images/social/magellan-share-preview.png',
  routes: [
    {
      path: '/',
      type: 'home',
      title: 'Magellan | Operating Structure for Trucking Companies',
      description: 'Magellan builds operating structure for trucking companies.',
      h1: 'Magellan',
    },
  ],
};

function getSeoConfig() {
  if (Array.isArray(seoConfig)) {
    const base = seoConfig.find((entry) => entry && Array.isArray(entry.routes)) || {};
    const looseRoutes = seoConfig.filter((entry) => entry && entry.path);
    const routes = [...(Array.isArray(base.routes) ? base.routes : []), ...looseRoutes];
    return {
      ...FALLBACK_SEO_CONFIG,
      ...base,
      routes: routes.length ? routes : FALLBACK_SEO_CONFIG.routes,
    };
  }

  return {
    ...FALLBACK_SEO_CONFIG,
    ...(seoConfig || {}),
    routes: Array.isArray(seoConfig?.routes) && seoConfig.routes.length ? seoConfig.routes : FALLBACK_SEO_CONFIG.routes,
  };
}

const CONFIG = getSeoConfig();

function ensureMeta(selector, create) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  return element;
}

function setMetaName(name, content) {
  const element = ensureMeta(`meta[name="${name}"]`, () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', name);
    return meta;
  });
  element.setAttribute('content', content || '');
}

function setMetaProperty(property, content) {
  const element = ensureMeta(`meta[property="${property}"]`, () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', property);
    return meta;
  });
  element.setAttribute('content', content || '');
}

function setCanonical(url) {
  const element = ensureMeta('link[rel="canonical"]', () => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    return link;
  });
  element.setAttribute('href', url);
}

export default function SeoRouteMetadata() {
  const location = useLocation();
  const [contentRoute, setContentRoute] = useState(null);
  useEffect(() => {
    const match = location.pathname.match(/^\/(speakers|webinars|services|blog|resources)\/([a-z0-9-]+)\/?$/);
    const controller = new AbortController();
    setContentRoute(null);
    if (match && !CONFIG.routes.some(route => route.path === location.pathname && route.path === '/webinars/company-training')) {
      const load = { speakers: getPublicSpeakerBySlug, services: getPublicService, webinars: getPublicCourseBySlug, blog: getPublishedBlogPostBySlug, resources: getPublishedResourcePageBySlug }[match[1]];
      load(match[2], controller.signal).then(record => {
        if (controller.signal.aborted) return;
        setContentRoute({ path: location.pathname, canonicalPath: record?.canonicalPath, type: { services: 'service', webinars: 'course', blog: 'article', resources: 'article' }[match[1]] || 'website', noindex: !record, omitCanonical: !record,
          title: record ? record.seoTitle || (match[1] === 'speakers' ? speakerDisplayName(record) : record.name || record.title) : 'Content unavailable | Magellan',
          description: record?.metaDescription || record?.excerpt || record?.description || record?.shortBio || '',
          publishedAt: record?.publishedAt, updatedAt: record?.updatedAt,
          image: record?.headshotUrl || record?.heroImageUrl || record?.featuredImageUrl || CONFIG.defaultImage,
          h1: (match[1] === 'speakers' ? speakerDisplayName(record) : record?.name || record?.title) || 'Content unavailable',
        });
      }).catch(() => { if (!controller.signal.aborted) setContentRoute(policy.unavailable(location.pathname)); });
    }
    return () => controller.abort();
  }, [location.pathname]);

  useEffect(() => {
    const path = policy.normalizePath(location.pathname);
    const dynamic = /^\/(speakers|services|resources|blog|webinars)\/[a-z0-9-]+$/.test(path)
      && path !== '/webinars/company-training' && !path.startsWith('/blog/category/');
    const route = policy.isPrivatePath(path) ? policy.findRoute(path, [])
      : dynamic ? policy.applyPolicy(contentRoute?.path === location.pathname ? contentRoute : policy.unavailable(path, true))
        : policy.findRoute(path, CONFIG.routes);
    const canonicalUrl = policy.canonicalUrl(route, CONFIG);
    const image = route.noindex ? '' : policy.imageUrl(route.image, CONFIG);

    document.title = route.title;
    setMetaName('description', route.description);
    setMetaName('robots', route.noindex ? 'noindex,nofollow' : 'index,follow');
    if (canonicalUrl) setCanonical(canonicalUrl);
    else document.head.querySelectorAll('link[rel="canonical"]').forEach(node => node.remove());
    setMetaProperty('og:type', route.type === 'article' ? 'article' : 'website');
    setMetaProperty('og:site_name', CONFIG.siteName);
    setMetaProperty('og:url', canonicalUrl);
    setMetaProperty('og:title', route.title);
    setMetaProperty('og:description', route.description);
    setMetaProperty('og:image', image);
    document.head.querySelectorAll('meta[name^="twitter:"]').forEach(node => node.remove());
    setMetaProperty('twitter:card', 'summary_large_image');
    setMetaProperty('twitter:url', canonicalUrl);
    setMetaProperty('twitter:title', route.title);
    setMetaProperty('twitter:description', route.description);
    setMetaProperty('twitter:image', image);

    // Static and hydrated output have one schema owner and identical graph rules.
    document.getElementById('magellan-static-schema')?.remove();
    document.getElementById('magellan-route-schema')?.remove();
    const graph = policy.buildSchema(route, CONFIG);
    if (graph) {
      const schema = document.createElement('script');
      schema.id = 'magellan-route-schema';
      schema.type = 'application/ld+json';
      schema.textContent = JSON.stringify(graph);
      document.head.appendChild(schema);
    }
  }, [contentRoute, location.pathname]);

  return null;
}
